import React, { useEffect, useState } from 'react'
import { Modal } from 'react-bootstrap'
import { useFormik } from 'formik'
import * as Yup from 'yup'

import { previewPayoutDisputeSuccess, resolveDepositDisputeSuccess, resolvePayoutDisputeSuccess } from '../../core/api'
import { buildDisputeErrorText, disputeErrorFromException } from '../../core/errors'
import { CulpritPreview, DisputeRecord, DisputeReasonLookupItem } from '../../core/response.types'
import { usePayoutCulpritDrafts } from '../../core/usePayoutCulpritDrafts'
import { DisputeResolveSummary, reasonChange } from '../DisputeResolveSummary/DisputeResolveSummary'
import { PayoutCulpritRemedyBlock } from '../PayoutCulpritRemedyBlock/PayoutCulpritRemedyBlock'
import { SettlementPreview, settlementErrorText } from '../../../../../settlement'

interface Props {
  show: boolean
  onClose: () => void
  dispute: DisputeRecord | null
  reasons: DisputeReasonLookupItem[]
  onResolved: () => void
}

interface FormShape {
  amount: string
}

const depositSchema = Yup.object().shape({
  amount: Yup.number()
    .typeError('Введите сумму')
    .moreThan(0, 'Сумма должна быть больше нуля')
    .required('Введите сумму'),
})

// Успех спора о выплате несёт факты по виновникам, а не сумму: их проверяет блок виновников.
const payoutSchema = Yup.object()

// A culprit whose preview found nothing to do does not block the success.
const previewAllows = (p: CulpritPreview): boolean => p.preview.canApply || p.preview.errorCode === 'REVISION_NOT_NEEDED'

export const ResolveSuccessModal: React.FC<Props> = ({ show, onClose, dispute, reasons, onResolved }) => {
  const [generalError, setGeneralError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [reasonId, setReasonId] = useState<number>(0)
  const isPayout = dispute?.type === 'PAYOUT_DISPUTE'
  const culprits = usePayoutCulpritDrafts(show && isPayout, dispute?.token ?? null, (dispute?.culprits ?? []).map((c) => c.token))
  const [previews, setPreviews] = useState<CulpritPreview[] | null>(null)
  const [previewing, setPreviewing] = useState(false)

  const showPreview = async () => {
    if (!dispute) return
    setPreviewing(true)
    setGeneralError(null)
    try {
      const r = await previewPayoutDisputeSuccess(dispute.id, { ...reasonChange(dispute, reasonId), culprits: culprits.facts })
      if (!r.data.success || !r.data.data) {
        setGeneralError(buildDisputeErrorText(r.data, 'Не удалось рассчитать проводки'))
        return
      }
      setPreviews(r.data.data)
    } catch (e: unknown) {
      setGeneralError(disputeErrorFromException(e, 'Не удалось рассчитать проводки'))
    } finally {
      setPreviewing(false)
    }
  }
  const payoutReady = previews !== null && previews.every(previewAllows)

  const formik = useFormik<FormShape>({
    initialValues: { amount: '' },
    validationSchema: isPayout ? payoutSchema : depositSchema,
    onSubmit: async (values) => {
      if (!dispute) return

      setSubmitting(true)
      setGeneralError(null)
      try {
        const r = isPayout
          ? await resolvePayoutDisputeSuccess(dispute.id, { ...reasonChange(dispute, reasonId), culprits: culprits.facts })
          : await resolveDepositDisputeSuccess(dispute.id, {
              amount: Number(values.amount),
              ...reasonChange(dispute, reasonId),
            })
        if (!r.data.success) {
          setGeneralError(buildDisputeErrorText(r.data, 'Не удалось завершить диспут'))
          return
        }
        onResolved()
        onClose()
      } catch (e: unknown) {
        setGeneralError(disputeErrorFromException(e, 'Не удалось завершить диспут'))
      } finally {
        setSubmitting(false)
      }
    },
  })

  useEffect(() => {
    if (show) {
      setGeneralError(null)
      setPreviews(null)
      formik.resetForm()
      if (dispute) setReasonId(dispute.reason.id)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, dispute?.id])

  if (!dispute) return null

  return (
    <Modal show={show} onHide={onClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>Завершить диспут успешно</Modal.Title>
      </Modal.Header>
      <form onSubmit={formik.handleSubmit}>
        <Modal.Body>
          <DisputeResolveSummary
            dispute={dispute}
            reasons={reasons}
            reasonId={reasonId}
            onReasonChange={setReasonId}
            disabled={submitting}
          />

          {isPayout ? (
            <div className='mb-2'>
              <div className='form-text mb-3'>
                По каждому виновнику укажите, сколько дошло получателю. Операция — сторно,
                корректировка или досылка — выводится из ответов; проводки видны до подтверждения.
              </div>
              {culprits.loading && <div className='text-muted'>Загрузка…</div>}
              {culprits.loadError && <div className='alert alert-danger'>{culprits.loadError}</div>}
              {culprits.candidates.map((c) => (
                <div key={c.token}>
                  <PayoutCulpritRemedyBlock
                    candidate={c}
                    draft={culprits.draftOf(c.token)}
                    disabled={submitting || previewing}
                    onChange={(draft) => {
                      culprits.setDraft(c.token, draft)
                      setPreviews(null)
                    }}
                  />
                  {previews
                    ?.filter((p) => p.token === c.token)
                    .map((p) =>
                      p.preview.canApply ? (
                        <SettlementPreview key={p.token} preview={p.preview} />
                      ) : (
                        <div key={p.token} className='text-muted small mb-3'>
                          {settlementErrorText(p.preview.errorCode ?? undefined, 'Операцию провести нельзя')}
                        </div>
                      )
                    )}
                </div>
              ))}
              <button
                type='button'
                className='btn btn-light-primary btn-sm'
                onClick={showPreview}
                disabled={!culprits.complete || previewing || submitting}
              >
                {previewing ? (
                  <>
                    <span className='spinner-border spinner-border-sm me-2' />
                    Считаем…
                  </>
                ) : (
                  'Показать проводки'
                )}
              </button>
            </div>
          ) : (
          <div className='mb-2'>
            <label className='form-label required' htmlFor='dispute-resolve-amount'>
              Сумма зачисления
            </label>
            <input
              id='dispute-resolve-amount'
              name='amount'
              type='number'
              step='0.01'
              min='0'
              className='form-control'
              placeholder='Сумма из чека'
              value={formik.values.amount}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              autoComplete='off'
            />
            {formik.touched.amount && formik.errors.amount && (
              <div className='text-danger small mt-1'>{formik.errors.amount}</div>
            )}
            <div className='form-text'>
              Введите сумму по чеку — она зачислится на тикет. Значения выше приведены для сверки и
              не подставляются: расхождение с ними обычно и есть предмет спора.
            </div>
          </div>
          )}

          {generalError && <div className='alert alert-danger mt-4 mb-0'>{generalError}</div>}
        </Modal.Body>
        <Modal.Footer>
          <button type='button' className='btn btn-light' onClick={onClose} disabled={submitting}>
            Отмена
          </button>
          <button type='submit' className='btn btn-success' disabled={submitting || (isPayout && !payoutReady)}>
            {submitting ? 'Завершаем…' : 'Завершить успешно'}
          </button>
        </Modal.Footer>
      </form>
    </Modal>
  )
}

export default ResolveSuccessModal
